import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MessageCircle, Save, RotateCcw, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UltraWhatsAppQuotas = () => {
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({});

  const token = localStorage.getItem('ultra_admin_token');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ultra-admin/whatsapp/quotas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotas(res.data.quotas || []);
      const d = {};
      (res.data.quotas || []).forEach(q => { d[q.plan_slug] = String(q.daily_limit); });
      setDrafts(d);
    } catch (e) {
      toast.error('Kotalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (planSlug) => {
    const val = Number(drafts[planSlug]);
    if (!Number.isFinite(val) || val < 0) {
      toast.error('Geçerli bir günlük limit girin (≥ 0)');
      return;
    }
    setSaving(s => ({ ...s, [planSlug]: true }));
    try {
      await axios.put(
        `${API}/ultra-admin/whatsapp/quotas/${planSlug}`,
        { daily_limit: val },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${planSlug} kotası kaydedildi`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(s => ({ ...s, [planSlug]: false }));
    }
  };

  const resetToDefault = async (q) => {
    setDrafts(d => ({ ...d, [q.plan_slug]: String(q.default_limit) }));
    // Save immediately to persist the default (creates an override that
    // matches the default — cleaner than deleting and having stale UI).
    setSaving(s => ({ ...s, [q.plan_slug]: true }));
    try {
      await axios.put(
        `${API}/ultra-admin/whatsapp/quotas/${q.plan_slug}`,
        { daily_limit: q.default_limit },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${q.plan_slug} varsayılana döndü`);
      await load();
    } catch (e) {
      toast.error('Sıfırlama başarısız');
    } finally {
      setSaving(s => ({ ...s, [q.plan_slug]: false }));
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="whatsapp-quotas-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-emerald-600" />
          WhatsApp Bildirim Kotaları
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Her plan için günlük gönderilebilir WhatsApp mesaj sayısını ayarlayın. Değişiklik anında geçerli.
        </p>
      </div>

      <Card className="p-4 mb-4 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            Twilio WhatsApp Sandbox modundadır. Test için alıcının önce
            <code className="bg-amber-100 px-1 rounded mx-1">+14155238886</code>
            numarasına WhatsApp&apos;tan
            <code className="bg-amber-100 px-1 rounded mx-1">join &lt;sandbox-keyword&gt;</code>
            yazması gerekir. Twilio Console → Messaging → Try WhatsApp.
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="p-8 text-gray-500">Yükleniyor…</div>
      ) : (
        <div className="space-y-3">
          {quotas.map(q => (
            <Card key={q.plan_slug} className="p-4 flex items-center gap-4" data-testid={`quota-row-${q.plan_slug}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900">{q.plan_name}</div>
                <div className="text-xs text-gray-500">
                  Slug: {q.plan_slug} · Varsayılan: {q.default_limit}/gün
                  {q.is_override && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-semibold">
                      OVERRIDE
                    </span>
                  )}
                </div>
              </div>
              <div className="w-28">
                <Label className="text-[10px] text-gray-500">Günlük limit</Label>
                <Input
                  type="number"
                  min={0}
                  value={drafts[q.plan_slug] ?? ''}
                  onChange={e => setDrafts(d => ({ ...d, [q.plan_slug]: e.target.value }))}
                  className="text-right"
                  data-testid={`quota-input-${q.plan_slug}`}
                />
              </div>
              <Button
                size="sm"
                onClick={() => save(q.plan_slug)}
                disabled={!!saving[q.plan_slug]}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`quota-save-${q.plan_slug}`}
              >
                <Save className="w-4 h-4 mr-1" />
                {saving[q.plan_slug] ? '…' : 'Kaydet'}
              </Button>
              {q.is_override && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resetToDefault(q)}
                  disabled={!!saving[q.plan_slug]}
                  data-testid={`quota-reset-${q.plan_slug}`}
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default UltraWhatsAppQuotas;
